import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Sparkles, Zap, Share2, Users, QrCode, Globe, ArrowRight, UserCircle, 
  Check, X, ChevronDown, Briefcase, User, Building2, Target, TrendingUp,
  Scan, Bell, Eye, Play, Phone, Mail, MapPin, Star, Shield, Clock,
  BarChart3, MessageSquare, Smartphone, CreditCard, Layout as LayoutIcon, Calendar, Send,
  Camera, Link as LinkIcon, Wrench, Video, FileText, ScanLine, Link2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import SignInModal from '../components/SignInModal';
import Chatbot from '../components/Chatbot';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { crmAPI } from '../services/api';
import SEO from '../components/SEO';
import { getOrganizationSchema, getWebsiteSchema, getBreadcrumbSchema } from '../utils/structuredData';
import { SEO_CONFIG, generateCanonicalUrl } from '../utils/seo';

// CSS Animations
const animationStyles = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(40px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fadeInLeft {
    from { opacity: 0; transform: translateX(-40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  @keyframes fadeInRight {
    from { opacity: 0; transform: translateX(40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }

  .animate-fadeInUp { animation: fadeInUp 0.7s ease-out forwards; }
  .animate-fadeInLeft { animation: fadeInLeft 0.7s ease-out forwards; }
  .animate-fadeInRight { animation: fadeInRight 0.7s ease-out forwards; }
  .animate-scaleIn { animation: scaleIn 0.6s ease-out forwards; }
  .animate-float { animation: float 3s ease-in-out infinite; }
  
  .animate-delay-100 { animation-delay: 0.1s; opacity: 0; }
  .animate-delay-200 { animation-delay: 0.2s; opacity: 0; }
  .animate-delay-300 { animation-delay: 0.3s; opacity: 0; }
  .animate-delay-400 { animation-delay: 0.4s; opacity: 0; }
  .animate-delay-500 { animation-delay: 0.5s; opacity: 0; }

  .scroll-reveal {
    opacity: 0;
    transform: translateY(40px);
    transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  .scroll-reveal.revealed {
    opacity: 1;
    transform: translateY(0);
  }
  
  .gradient-text {
    background: linear-gradient(135deg, #f59e0b 0%, #ea580c 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .hero-gradient {
    background: linear-gradient(135deg, #1f2937 0%, #111827 50%, #0f172a 100%);
  }
  
  .card-hover {
    transition: all 0.3s ease;
  }
  
  .card-hover:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  }
`;

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInRedirectPath, setSignInRedirectPath] = useState('/dashboard');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Demo booking modal state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    message: ''
  });
  const [isSubmittingDemo, setIsSubmittingDemo] = useState(false);
  const [demoSuccess, setDemoSuccess] = useState(false);

  // Handle demo form submission
  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    
    if (!demoForm.name.trim()) {
      toast({ title: "Name required", description: "Please enter your name", variant: "destructive" });
      return;
    }
    
    if (!demoForm.email.trim() && !demoForm.phone.trim()) {
      toast({ title: "Contact required", description: "Please enter your email or phone number", variant: "destructive" });
      return;
    }
    
    setIsSubmittingDemo(true);
    
    try {
      const demoData = {
        name: demoForm.name.trim(),
        email: demoForm.email.trim() || null,
        phone: demoForm.phone.trim() ? `${demoForm.countryCode}${demoForm.phone.trim()}` : null,
        message: demoForm.message.trim() || null,
        source: "Demo Request - Home Page"
      };
      
      // Submit to mraja's leads (main demo account)
      await crmAPI.submitDemoRequest('mraja', demoData);
      
      setDemoSuccess(true);
      toast({ title: "Demo Request Sent!", description: "We'll contact you soon." });
      
      // Reset after delay
      setTimeout(() => {
        setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
        setDemoSuccess(false);
        setShowDemoModal(false);
      }, 2000);
      
    } catch (error) {
      console.error('Demo request error:', error);
      toast({ title: "Error", description: "Failed to submit. Please try again.", variant: "destructive" });
    } finally {
      setIsSubmittingDemo(false);
    }
  };

  // Scroll reveal effect
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.scroll-reveal');
    revealElements.forEach(el => observer.observe(el));

    return () => {
      revealElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  // Structured data
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      getOrganizationSchema(),
      getWebsiteSchema(),
      getBreadcrumbSchema([{ name: 'Home', path: '/' }]),
      {
        '@type': 'SoftwareApplication',
        name: 'Piper Card',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '999',
          priceCurrency: 'INR'
        }
      }
    ]
  };

  const handleGetStarted = () => {
    // Redirect to public card builder - no auth required to start
    navigate('/card-builder');
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Turn Every Business Meeting Into a Captured Lead | PiperCard"
        description="PiperCard transforms your QR codes into a lead-generating system. Track every scan, capture visitor notes, and follow up faster. Built for sales professionals, real estate brokers, and insurance agents."
        keywords="lead capture, digital business card, QR code tracking, sales leads, real estate marketing, insurance agent tools, visitor tracking, CRM, business networking"
        canonicalUrl={generateCanonicalUrl('/')}
        structuredData={structuredData}
      />
      
      <style>{animationStyles}</style>

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              {/* Mobile Menu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-gray-800 text-white"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
              
              <Link to="/" className="flex items-center space-x-2">
                <img src="/logo.png" alt="Piper Card" className="h-10 w-auto" />
                <span className="text-xl font-bold text-amber-500">Piper Card</span>
              </Link>
            </div>

            <nav className="hidden md:flex items-center space-x-6">
              <a href="#features" className="text-gray-300 hover:text-amber-400 font-medium transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-300 hover:text-amber-400 font-medium transition-colors">How It Works</a>
              
              {/* Tools Dropdown Menu - Only show when not logged in */}
              {!user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-1 text-gray-300 hover:text-amber-400 font-medium transition-colors"
                      data-testid="home-tools-dropdown-trigger"
                    >
                      <span>Tools</span>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="start" 
                    className="w-64 bg-gray-900 border-gray-700 text-gray-100"
                    data-testid="home-tools-dropdown-content"
                  >
                    <DropdownMenuLabel className="text-amber-400 text-xs uppercase tracking-wider">
                      Free Marketing Tools
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-gray-700" />
                  
                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 focus:text-white"
                    onClick={() => navigate('/email-signature-generator')}
                    data-testid="home-tools-menu-email-signature"
                  >
                    <Mail className="w-4 h-4 mr-2 text-amber-400" />
                    <div>
                      <div className="font-medium">Email Signature Generator</div>
                      <div className="text-xs text-gray-400">Create professional email signatures</div>
                    </div>
                  </DropdownMenuItem>

                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 focus:text-white"
                    onClick={() => navigate('/meeting-themes-generator')}
                    data-testid="home-tools-menu-meeting-themes"
                  >
                    <Video className="w-4 h-4 mr-2 text-blue-400" />
                    <div>
                      <div className="font-medium">Meeting Background Generator</div>
                      <div className="text-xs text-gray-400">Custom Zoom/Teams backgrounds</div>
                    </div>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator className="bg-gray-700" />

                  <DropdownMenuItem 
                    className="cursor-pointer hover:bg-gray-800 focus:bg-gray-800 focus:text-white"
                    onClick={() => navigate('/card-builder')}
                    data-testid="home-tools-menu-digital-card"
                  >
                    <CreditCard className="w-4 h-4 mr-2 text-amber-400" />
                    <div>
                      <div className="font-medium">Digital Business Card Maker</div>
                      <div className="text-xs text-gray-400">Create your digital card</div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              <Link to="/pricing" className="text-gray-300 hover:text-amber-400 font-medium transition-colors">Pricing</Link>
              <Link to="/shop" className="text-gray-300 hover:text-amber-400 font-medium transition-colors">Shop</Link>
              <Link to="/blog" className="text-gray-300 hover:text-amber-400 font-medium transition-colors">Blog</Link>
              
              {!user ? (
                <>
                  <Button 
                    onClick={() => setShowDemoModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-6"
                    data-testid="header-book-demo-btn"
                  >
                    Book Demo
                  </Button>
                  <Button 
                    onClick={handleGetStarted}
                    variant="outline"
                    className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-white font-bold px-6"
                    data-testid="header-start-trial-btn"
                  >
                    Start Free Trial
                  </Button>
                  <Button 
                    onClick={() => { setSignInRedirectPath('/dashboard'); setShowSignInModal(true); }}
                    variant="ghost"
                    className="text-gray-300 hover:text-white font-medium"
                    data-testid="header-sign-in-btn"
                  >
                    Sign In
                  </Button>
                </>
              ) : (
                <Link to="/dashboard">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-6">
                    Dashboard
                  </Button>
                </Link>
              )}
            </nav>

            <div className="md:hidden flex items-center gap-2">
              {!user ? (
                <>
                  <Button 
                    onClick={() => setShowDemoModal(true)} 
                    size="sm" 
                    className="bg-amber-500 text-white font-bold"
                    data-testid="mobile-book-demo-btn"
                  >
                    Book Demo
                  </Button>
                  <Button 
                    onClick={() => { setSignInRedirectPath('/dashboard'); setShowSignInModal(true); }} 
                    size="sm" 
                    variant="ghost"
                    className="text-gray-300 hover:text-white"
                    data-testid="mobile-sign-in-btn"
                  >
                    Sign In
                  </Button>
                </>
              ) : (
                <Link to="/dashboard">
                  <Button size="sm" className="bg-amber-500 text-white font-bold">Dashboard</Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-gray-800 px-4 py-4 space-y-2">
            <a href="#features" className="block px-4 py-3 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#how-it-works" className="block px-4 py-3 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
            <a href="#industries" className="block px-4 py-3 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Industries</a>
            
            {/* Tools Section - Only show when not logged in */}
            {!user && (
              <div className="border-t border-gray-800 pt-2 mt-2">
                <span className="block px-4 py-2 text-amber-400 font-semibold text-sm uppercase tracking-wider">Free Tools</span>
                <Link to="/email-signature-generator" className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                  <Mail className="w-4 h-4" /> Email Signature Generator
                </Link>
                <Link to="/meeting-themes-generator" className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                  <Video className="w-4 h-4" /> Meeting Background Generator
                </Link>
                <Link to="/card-builder" className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>
                  <CreditCard className="w-4 h-4" /> Digital Business Card Maker
                </Link>
              </div>
            )}
            
            <div className="border-t border-gray-800 pt-2 mt-2">
              <Link to="/pricing" className="block px-4 py-3 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <Link to="/shop" className="block px-4 py-3 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Shop</Link>
              <Link to="/blog" className="block px-4 py-3 text-gray-300 hover:text-amber-400 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
            </div>
          </div>
        )}
      </header>

      {/* HERO SECTION - Sales Focused */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="text-center lg:text-left">
              <div className="animate-fadeInUp inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
                <Target className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-semibold">Lead Capture System for Sales Professionals</span>
              </div>

              <h1 className="animate-fadeInUp animate-delay-100 text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Turn Every Business Meeting Into a{' '}
                <span className="gradient-text">Captured Lead.</span>
              </h1>

              <p className="animate-fadeInUp animate-delay-200 text-lg sm:text-xl text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0">
                PiperCard transforms your QR codes, posters, and social media into a lead-generating system. 
                Track every scan, capture visitor notes, and follow up faster — so you <strong className="text-white">never lose a potential client again.</strong>
              </p>

              <div className="animate-fadeInUp animate-delay-300 flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-8">
                <Button
                  onClick={() => setShowDemoModal(true)}
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-amber-500/25 w-full sm:w-auto"
                  data-testid="hero-book-demo-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Your Free Demo
                </Button>
                <Button
                  onClick={handleGetStarted}
                  variant="outline"
                  size="lg"
                  className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-white font-bold px-8 py-6 text-lg w-full sm:w-auto transition-all"
                  data-testid="hero-start-trial-btn"
                >
                  Start Free Trial
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              <div className="animate-fadeInUp animate-delay-400 flex flex-wrap items-center justify-center lg:justify-start gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Setup in 2 minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Free forever plan</span>
                </div>
              </div>
            </div>

            {/* Right - Visual */}
            <div className="animate-fadeInRight animate-delay-200 relative hidden lg:block">
              <div className="relative">
                {/* Dashboard Preview Card */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">Lead Dashboard</h3>
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Live</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                      <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">Rahul Sharma</p>
                        <p className="text-xs text-gray-500">Scanned from property poster</p>
                      </div>
                      <Bell className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">Priya Patel</p>
                        <p className="text-xs text-gray-500">Interested in insurance policy</p>
                      </div>
                      <MessageSquare className="w-5 h-5 text-teal-500" />
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-10 h-10 bg-violet-500 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm">Amit Kumar</p>
                        <p className="text-xs text-gray-500">WhatsApp forward scan</p>
                      </div>
                      <Phone className="w-5 h-5 text-violet-500" />
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between text-sm">
                    <span className="text-gray-500">Today's Leads</span>
                    <span className="font-bold text-amber-600">+12 new</span>
                  </div>
                </div>

                {/* Floating Stats */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">89%</p>
                      <p className="text-xs text-gray-500">More leads captured</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-500/5 to-transparent pointer-events-none"></div>
      </section>

      {/* PROBLEM SECTION */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="scroll-reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full mb-6">
              <X className="w-4 h-4 text-red-600" />
              <span className="text-red-700 text-sm font-semibold">The Problem</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Still Losing Leads After Sharing Your Visiting Card?
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
              <div className="flex items-start gap-3 text-left p-4 bg-white rounded-xl border border-red-100">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">You don't know who scanned your QR</span>
              </div>
              <div className="flex items-start gap-3 text-left p-4 bg-white rounded-xl border border-red-100">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Interested buyers forget to call back</span>
              </div>
              <div className="flex items-start gap-3 text-left p-4 bg-white rounded-xl border border-red-100">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Paper visiting cards get lost or thrown</span>
              </div>
              <div className="flex items-start gap-3 text-left p-4 bg-white rounded-xl border border-red-100">
                <X className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">No way to track interest from your ads</span>
              </div>
            </div>

            <div className="inline-block px-6 py-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-red-700 font-semibold">Every missed enquiry is lost revenue.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="scroll-reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full mb-6">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-green-700 text-sm font-semibold">The Solution</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              A Digital Card That Works Like a <span className="gradient-text">Sales Machine</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Transform every QR scan from your posters, ads, and videos into a trackable lead
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Feature 1 */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-amber-200">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                <QrCode className="w-7 h-7 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Smart QR Codes</h3>
              <p className="text-gray-600">
                Share your profile instantly via QR. Print on posters, banners, pamphlets, videos — anywhere.
              </p>
            </Card>

            {/* Feature 2 */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-teal-200">
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-4">
                <MessageSquare className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Capture Visitor Notes</h3>
              <p className="text-gray-600">
                Visitors can send you notes with their interest. Know exactly what they're looking for.
              </p>
            </Card>

            {/* Feature 3 */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-violet-200">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                <BarChart3 className="w-7 h-7 text-violet-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Track Every Scan</h3>
              <p className="text-gray-600">
                See who viewed your card with analytics dashboard. Name, phone, location — all captured.
              </p>
            </Card>

            {/* Feature 4 */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-rose-200">
              <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center mb-4">
                <Bell className="w-7 h-7 text-rose-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Instant Notifications</h3>
              <p className="text-gray-600">
                Get alerts when someone scans your QR or submits their details. Follow up while they're still interested.
              </p>
            </Card>

            {/* Feature 5 */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-blue-200">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                <BarChart3 className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Profile Analytics</h3>
              <p className="text-gray-600">
                Track who views your profile, when they visit, and which links they click. Data-driven networking.
              </p>
            </Card>

            {/* Feature 6 */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-emerald-200">
              <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                <Share2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Share Anywhere</h3>
              <p className="text-gray-600">
                WhatsApp, Email, Social Media, Email Signature — share your card everywhere with one tap.
              </p>
            </Card>

            {/* Feature 7 - Email Signature */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-pink-200">
              <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center mb-4">
                <Mail className="w-7 h-7 text-pink-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Signature Generator</h3>
              <p className="text-gray-600">
                Create professional email signatures with your photo, QR code, and contact info. Works in Gmail & Outlook.
              </p>
            </Card>

            {/* Feature 8 - Meeting Backgrounds */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-indigo-200">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Meeting Backgrounds</h3>
              <p className="text-gray-600">
                Download branded virtual backgrounds for Zoom, Meet & Teams with your info and QR code built-in.
              </p>
            </Card>

            {/* Feature 9 - NFC Cards */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100 hover:border-orange-200">
              <div className="w-14 h-14 bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
                <CreditCard className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">NFC Business Cards</h3>
              <p className="text-gray-600">
                Order physical NFC cards. Just tap on any phone to share your digital profile instantly.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="scroll-reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-6">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-semibold">Simple 3-Step Process</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How PiperCard Works
            </h2>
            <p className="text-lg text-gray-400">Start capturing leads in under 2 minutes</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="scroll-reveal text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                1
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Create Your Profile</h3>
              <p className="text-gray-400">
                Add your photo, contact info, social links. Choose from professional templates. Takes 2 minutes.
              </p>
            </div>

            {/* Step 2 */}
            <div className="scroll-reveal text-center">
              <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                2
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Share or Print Your QR</h3>
              <p className="text-gray-400">
                Print QR on posters, banners, pamphlets. Share via WhatsApp, email, social media. Use NFC cards.
              </p>
            </div>

            {/* Step 3 */}
            <div className="scroll-reveal text-center">
              <div className="w-16 h-16 bg-violet-500 rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">
                3
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Capture & Follow Up</h3>
              <p className="text-gray-400">
                Every scan is tracked. Visitors submit their details. You get notifications. Close more deals.
              </p>
            </div>
          </div>

          <div className="scroll-reveal text-center mt-12">
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-6 text-lg"
            >
              Create Your Free Card
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* INDUSTRY USE CASES */}
      <section id="industries" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="scroll-reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 rounded-full mb-6">
              <Users className="w-4 h-4 text-violet-600" />
              <span className="text-violet-700 text-sm font-semibold">Industry Solutions</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Built for Professionals Who <span className="gradient-text">Meet Clients Daily</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Real Estate */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Real Estate Brokers</h3>
                  <p className="text-gray-600 mb-4">
                    Turn property posters into automatic enquiry collectors. Every property brochure and WhatsApp forward becomes a lead machine.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-amber-600 font-semibold">
                    <Check className="w-4 h-4" />
                    <span>Perfect for property listings & site visits</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Insurance */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Shield className="w-7 h-7 text-teal-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Insurance Agents</h3>
                  <p className="text-gray-600 mb-4">
                    Capture interested policy buyers instantly from WhatsApp forwards and flyers. Know exactly what policy they're interested in.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-teal-600 font-semibold">
                    <Check className="w-4 h-4" />
                    <span>Capture policy interest with visitor notes</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Sales Teams */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-7 h-7 text-violet-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Sales Teams</h3>
                  <p className="text-gray-600 mb-4">
                    Track every interaction and follow up with data. Your sales team always has ready customers to contact.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-violet-600 font-semibold">
                    <Check className="w-4 h-4" />
                    <span>Lead capture & instant notifications</span>
                  </div>
                </div>
              </div>
            </Card>

            {/* Consultants */}
            <Card className="scroll-reveal card-hover p-6 border-2 border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-rose-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-7 h-7 text-rose-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Consultants & Founders</h3>
                  <p className="text-gray-600 mb-4">
                    Look premium and never miss networking opportunities. Perfect for conferences, events, and meetings.
                  </p>
                  <div className="flex items-center gap-2 text-sm text-rose-600 font-semibold">
                    <Check className="w-4 h-4" />
                    <span>Professional bio pages with analytics</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* ROI SECTION */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-500 to-orange-500">
        <div className="max-w-4xl mx-auto text-center">
          <div className="scroll-reveal">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              If One Extra Deal Closes, PiperCard Pays for Itself.
            </h2>
            <p className="text-xl text-amber-100 mb-8 max-w-2xl mx-auto">
              Whether you sell property, insurance, services, or products — capturing even one extra serious lead can generate thousands in revenue.
            </p>
            <div className="inline-block px-8 py-4 bg-white/20 backdrop-blur rounded-2xl">
              <p className="text-white font-semibold text-lg">
                NFC Card at just <span className="text-3xl font-bold">₹999</span> + Free Premium Features worth ₹999
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FREE TOOLS SECTION */}
      <section id="tools" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="scroll-reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              100% Free Tools
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Free Professional Tools
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to create a professional digital presence. All tools are completely free.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: 'Email Signature', desc: 'Professional HTML signatures', icon: Mail, path: '/email-signature-generator', gradient: 'from-blue-500 to-cyan-500' },
              { title: 'Meeting Backgrounds', desc: 'Branded video call backdrops', icon: Play, path: '/meeting-themes-generator', gradient: 'from-purple-500 to-pink-500' },
              { title: 'Digital Business Card', desc: 'Create shareable digital cards', icon: CreditCard, path: '/card-builder', gradient: 'from-green-500 to-emerald-500' },
            ].map((tool, index) => (
              <Link
                key={index}
                to={tool.path}
                className="group"
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-1">
                  <div className={`bg-gradient-to-r ${tool.gradient} p-4 text-white`}>
                    <tool.icon className="w-7 h-7" />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-gray-900 mb-1 text-sm">{tool.title}</h3>
                    <p className="text-xs text-gray-600">{tool.desc}</p>
                    <div className="flex items-center mt-2 text-amber-600 font-medium text-xs group-hover:gap-1 transition-all">
                      <span>Try Free</span>
                      <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="scroll-reveal text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              PiperCard vs Normal Visiting Card
            </h2>
          </div>

          <div className="scroll-reveal bg-white rounded-2xl shadow-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-900">
                  <th className="text-left py-4 px-6 text-white font-semibold">Feature</th>
                  <th className="text-center py-4 px-6">
                    <span className="text-amber-400 font-bold">PiperCard</span>
                  </th>
                  <th className="text-center py-4 px-6">
                    <span className="text-gray-400 font-bold">Paper Card</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr>
                  <td className="py-4 px-6 text-gray-700 font-medium">Track who viewed</td>
                  <td className="py-4 px-6 text-center">
                    <Check className="w-6 h-6 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="w-6 h-6 text-gray-300 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="py-4 px-6 text-gray-700 font-medium">Capture visitor notes</td>
                  <td className="py-4 px-6 text-center">
                    <Check className="w-6 h-6 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="w-6 h-6 text-gray-300 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-700 font-medium">Always accessible</td>
                  <td className="py-4 px-6 text-center">
                    <Check className="w-6 h-6 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="w-6 h-6 text-gray-300 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="py-4 px-6 text-gray-700 font-medium">Update anytime</td>
                  <td className="py-4 px-6 text-center">
                    <Check className="w-6 h-6 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="w-6 h-6 text-gray-300 mx-auto" />
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-6 text-gray-700 font-medium">Profile Analytics</td>
                  <td className="py-4 px-6 text-center">
                    <Check className="w-6 h-6 text-green-500 mx-auto" />
                  </td>
                  <td className="py-4 px-6 text-center">
                    <X className="w-6 h-6 text-gray-300 mx-auto" />
                  </td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="py-4 px-6 text-gray-700 font-medium">Cost per year</td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-amber-600 font-bold">₹999</span>
                  </td>
                  <td className="py-4 px-6 text-center">
                    <span className="text-gray-500">₹4,000+ (reprints)</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* NFC CARD SECTION */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div className="scroll-reveal text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/30 rounded-full mb-6">
                <Smartphone className="w-4 h-4 text-teal-400" />
                <span className="text-teal-400 text-sm font-semibold">Premium Hardware</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Upgrade to Smart <span className="text-teal-400">NFC Business Cards</span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-8">
                <strong className="text-white">Tap. Share. Capture.</strong> One tap shares your entire profile. No app needed for the receiver.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-teal-400" />
                  </div>
                  <span className="text-gray-300">Premium NFC Card (Metal/PVC options)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-teal-400" />
                  </div>
                  <span className="text-gray-300">1 Year Premium Access Included (₹999 value FREE)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-teal-400" />
                  </div>
                  <span className="text-gray-300">Full Lead Tracking & Analytics Enabled</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-teal-500/20 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-teal-400" />
                  </div>
                  <span className="text-gray-300">Works with iPhone & Android</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Link to="/shop">
                  <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white font-bold px-8">
                    Order Your Smart Card
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <span className="text-gray-400 text-sm">Starting at ₹999 + Free Premium</span>
              </div>
            </div>

            {/* Right - Visual Card */}
            <div className="scroll-reveal relative">
              <div className="relative mx-auto w-72 h-44 bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                {/* Card Design */}
                <div className="absolute inset-0 p-6 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-bold text-lg">Your Name</p>
                      <p className="text-gray-400 text-sm">Sales Professional</p>
                    </div>
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="flex justify-between items-end">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-white/10 rounded flex items-center justify-center">
                        <QrCode className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-gray-400 text-xs">NFC Enabled</span>
                    </div>
                    <img src="/logo.png" alt="Piper Card" className="h-6 opacity-50" />
                  </div>
                </div>
                
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-teal-500/20 rounded-3xl blur-xl -z-10"></div>
              </div>

              {/* Floating Badge */}
              <div className="absolute -bottom-4 -right-4 bg-teal-500 text-white px-4 py-2 rounded-xl shadow-lg animate-float">
                <p className="text-sm font-bold">Tap to Share</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="scroll-reveal text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "What is PiperCard?",
                a: "PiperCard is a lead capture system disguised as a digital business card. Create your profile, share via QR or NFC, and capture visitor details automatically. Know who's interested and follow up faster."
              },
              {
                q: "How does lead capture work?",
                a: "When someone scans your QR code or views your profile, they can submit their contact details and notes (like what they're interested in). You get instant notifications and can add them to your CRM pipeline."
              },
              {
                q: "Can I print the QR on my marketing materials?",
                a: "Absolutely! Print your PiperCard QR on posters, banners, pamphlets, business cards, videos — anywhere. Every scan is tracked and leads are captured automatically."
              },
              {
                q: "How is pricing structured?",
                a: "₹999 for NFC card with 1 year free premium features (worth ₹999). Volume discounts available for teams. Free plan also available with basic features."
              },
              {
                q: "Do people need an account to view my card?",
                a: "No! Anyone can view your digital profile by scanning your QR code or clicking your link. They can submit their details without signing up."
              },
              {
                q: "What's included with NFC cards?",
                a: "Physical NFC card + 1 year Pro subscription included. Just tap against any smartphone to share your profile. Works with iPhone and Android."
              }
            ].map((faq, index) => (
              <Card 
                key={index}
                className="border-2 border-gray-200 hover:border-amber-300 transition-all"
              >
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full text-left p-6 flex items-center justify-between gap-4"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-amber-600 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6 text-gray-600 border-t border-gray-100 pt-4">
                    {faq.a}
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="scroll-reveal">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Stop Losing Leads. Start Tracking Them.
            </h2>
            <p className="text-xl text-gray-400 mb-10">
              Join professionals who never miss a potential client again.
            </p>
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-10 py-6 text-lg shadow-lg shadow-amber-500/25"
            >
              Create Your Free Card Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-gray-500 text-sm mt-4">No credit card required. Setup takes less than 2 minutes.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="Piper Card" className="h-8 w-auto" />
                <span className="text-white font-bold">Piper Card</span>
              </div>
              <p className="text-gray-400 text-sm">Lead capture system for sales professionals.</p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <div className="space-y-2 text-sm">
                <a href="#features" className="block text-gray-400 hover:text-amber-400">Features</a>
                <Link to="/pricing" className="block text-gray-400 hover:text-amber-400">Pricing</Link>
                <Link to="/shop" className="block text-gray-400 hover:text-amber-400">NFC Cards</Link>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <div className="space-y-2 text-sm">
                <a href="#how-it-works" className="block text-gray-400 hover:text-amber-400">How It Works</a>
                <a href="#industries" className="block text-gray-400 hover:text-amber-400">Industries</a>
                <Link to="/blog" className="block text-gray-400 hover:text-amber-400">Blog</Link>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <div className="space-y-2 text-sm">
                <Link to="/terms-and-conditions" className="block text-gray-400 hover:text-amber-400">Terms</Link>
                <Link to="/privacy-policy" className="block text-gray-400 hover:text-amber-400">Privacy</Link>
                <a href="mailto:contact@allgrowtechnologies.com" className="block text-gray-400 hover:text-amber-400">Contact</a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Piper Card by Allgrow Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <SignInModal 
        open={showSignInModal} 
        onOpenChange={setShowSignInModal}
        redirectPath={signInRedirectPath}
      />
      
      {/* Demo Booking Modal */}
      <Dialog open={showDemoModal} onOpenChange={(open) => {
        if (!isSubmittingDemo) {
          setShowDemoModal(open);
          if (!open) {
            setDemoForm({ name: '', email: '', phone: '', countryCode: '+91', message: '' });
            setDemoSuccess(false);
          }
        }
      }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto p-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-5 text-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold">Book Your Free Demo</h2>
                  <p className="text-sm text-white/80">Get a personalized walkthrough</p>
                </div>
              </div>
              <button 
                onClick={() => !isSubmittingDemo && setShowDemoModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                data-testid="close-demo-modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {demoSuccess ? (
            // Success State
            <div className="px-5 py-10 text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Request Sent!</h3>
              <p className="text-gray-600">We'll get back to you shortly.</p>
            </div>
          ) : (
            // Form
            <form onSubmit={handleDemoSubmit} className="px-5 py-5 space-y-4">
              {/* Name Input */}
              <div className="space-y-1.5">
                <Label htmlFor="demo-name" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <User className="w-4 h-4 text-violet-500" />
                  Your Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="demo-name"
                  placeholder="Enter your full name"
                  value={demoForm.name}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full h-12 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                  data-testid="demo-name-input"
                />
              </div>

              {/* Email Input */}
              <div className="space-y-1.5">
                <Label htmlFor="demo-email" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-violet-500" />
                  Email
                </Label>
                <Input
                  id="demo-email"
                  type="email"
                  placeholder="your@email.com"
                  value={demoForm.email}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full h-12 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                  data-testid="demo-email-input"
                />
              </div>

              {/* Phone Input */}
              <div className="space-y-1.5">
                <Label htmlFor="demo-phone" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <Phone className="w-4 h-4 text-violet-500" />
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <CountryCodeSelect
                    value={demoForm.countryCode}
                    onChange={(e) => setDemoForm(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="w-24 h-12 text-sm rounded-xl"
                  />
                  <Input
                    id="demo-phone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    value={demoForm.phone}
                    onChange={(e) => setDemoForm(prev => ({ ...prev, phone: e.target.value.replace(/[^\d]/g, '') }))}
                    className="flex-1 h-12 text-base border-gray-200 focus:border-violet-500 focus:ring-violet-500 rounded-xl"
                    data-testid="demo-phone-input"
                  />
                </div>
              </div>

              {/* Message Input */}
              <div className="space-y-1.5">
                <Label htmlFor="demo-message" className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                  <MessageSquare className="w-4 h-4 text-violet-500" />
                  What are you interested in? <span className="text-gray-400 font-normal">(Optional)</span>
                </Label>
                <textarea
                  id="demo-message"
                  placeholder="Tell us about your requirements..."
                  value={demoForm.message}
                  onChange={(e) => setDemoForm(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full px-4 py-3 text-base border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                  data-testid="demo-message-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmittingDemo}
                className="w-full h-12 text-base bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 active:scale-[0.98] transition-all"
                data-testid="submit-demo-btn"
              >
                {isSubmittingDemo ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Book Free Demo
                  </>
                )}
              </Button>

              {/* Privacy note */}
              <p className="text-xs text-center text-gray-400 pt-2">
                Your information will be used to contact you about our services.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      <Chatbot />
    </div>
  );
};

export default Home;
